import {
  Flex,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Text as ChakraText,
  useColorModeValue,
} from "@chakra-ui/react";
import Card from "../../components/Card/Card";
import React, { useState, useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";
import API_CONFIG from "../../Configuration/apiConfig";
import apiClient from "../../Configuration/apiClient";
import { FaAngleRight, FaArrowLeft } from "react-icons/fa";
import { saveAs } from "file-saver";
import handlePrintReport from "../Reports/ImageSummaryReport";
import { exportNestedToCsv } from "../../Utils/ExcelExport";
import AgGridWrapper from "../../components/AgGrid/AgGridWrapper";

export default function InventoryDashboard() {
  const textColor = useColorModeValue("gray.700", "white");
  const history = useHistory();
  const location = useLocation();
  const [detailViewData, setDetailViewData] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);

  // Get runId from URL query parameters
  const query = new URLSearchParams(location.search);
  const runId = query.get("runId") || "1";

  // Fetch table data
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await apiClient.get(
          `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.BATCH_TRANSACTIONS.GROUPED}?runId=${encodeURIComponent(runId)}`
        );
        const apiData = Array.isArray(response.data) ? response.data : [response.data];

        const transformedData = await Promise.all(
          apiData.map(async (item, index) => {
            const productId = item.productTransaction?.product?.productId ?? "";
            const companyName =
              item.productTransaction?.product?.productCompany?.productCompanyName ??
              "Unknown";
            const brandName =
              item.productTransaction?.product?.productBrand?.productBrandName ??
              "Unknown";

            let additionalData = [];
            try {
              const encodedUrl = encodeURI(
                `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.BATCH_TRANSACTIONS.GROUPED_BY_PRODUCT.replace(
                  ":runId",
                  runId
                )
                  .replace(":productId", productId)
                  .replace(":companyName", companyName)
                  .replace(":brandName", brandName)}`
              );
              const secondResponse = await apiClient.get(encodedUrl);
              additionalData = Array.isArray(secondResponse.data)
                ? secondResponse.data
                : [secondResponse.data];
            } catch (error) {
              console.error(
                `Error fetching additional data for ${companyName}/${brandName}: ${error.message}`
              );
            }

            return {
              rowIndex: index,
              sNo: item.sno ?? index + 1,
              company: companyName,
              brand: brandName,
              type: item.productTransaction?.product?.packType ?? "Unknown",
              size: item.productTransaction?.product?.packSize ?? "Unknown",
              productCount: item.productTransaction?.productCount ?? 0,
              additionalData: additionalData.map((data) => ({
                file: data.batchWiseFileTransactions?.fileName ?? "N/A",
                company:
                  data.batchWiseFileTransactions?.product?.productCompany
                    ?.productCompanyName ?? companyName,
                brand:
                  data.batchWiseFileTransactions?.product?.productBrand
                    ?.productBrandName ?? brandName,
                type: data.batchWiseFileTransactions?.product?.packType ?? "Unknown",
                size: data.batchWiseFileTransactions?.product?.packSize ?? "Unknown",
                count: data.batchWiseFileTransactions?.productCount ?? 0,
              })),
            };
          })
        );

        setDetailViewData(transformedData);
      } catch (error) {
        console.error("Error fetching data:", error.message);
        setError("Failed to load data. Please check the API or network.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [runId]);

  const handleViewDetails = (rowData) => {
    setSelectedRowData(rowData);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedRowData(null);
  };

  const handleBackToDashboard = () => {
    history.push("/admin/dashboard");
  };

  const handleExportData = () => {
    exportNestedToCsv({
      headers: ["S.No", "Company", "Brand", "Type", "Size", "Product Count", "Nested File", "Nested Company", "Nested Brand", "Nested Pack Type", "Nested Pack Size", "Nested Count"],
      data: detailViewData,
      getMainValues: (row) => [row.sNo, row.company, row.brand, row.type, row.size, row.productCount],
      getNestedValues: (nested) => [nested.file || "", nested.company || "", nested.brand || "", nested.type || "", nested.size || "", nested.count || ""],
      fileName: "abstract_view",
    });
  };

  // Main table column definitions
  const columnDefs = useMemo(
    () => [
      {
        headerName: "",
        field: "expand",
        width: 30,
        cellRenderer: (params) => {
          if (params.data.additionalData?.length > 0) {
            return (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                }}
              >
                <div
                  onClick={() => handleViewDetails(params.data)}
                  style={{ cursor: "pointer" }}
                >
                  <FaAngleRight size={20} />
                </div>
              </div>
            );
          }
          return null;
        },
        sortable: false,
        filter: false,
      },
      {
        headerName: "S.No",
        field: "sNo",
        sortable: true,
        filter: true,
        width: 100,
      },
      {
        headerName: "Company",
        field: "company",
        sortable: true,
        filter: true,
      },
      {
        headerName: "Brand",
        field: "brand",
        sortable: true,
        filter: true,
      },
      {
        headerName: "Type",
        field: "type",
        sortable: true,
        filter: true,
      },
      {
        headerName: "Size",
        field: "size",
        sortable: true,
        filter: true,
      },
      {
        headerName: "Product Count",
        field: "productCount",
        sortable: true,
        filter: "agNumberColumnFilter",
        cellClass: "ag-numeric-cell",
        valueFormatter: (params) => params.value.toLocaleString(),
      },
    ],
    []
  );

  // Nested table column definitions
  const nestedColumnDefs = useMemo(
    () => [
      { headerName: "File", field: "file", sortable: true, filter: true },
      { headerName: "Company", field: "company", sortable: true, filter: true },
      { headerName: "Brand", field: "brand", sortable: true, filter: true },
      { headerName: "Pack Type", field: "type", sortable: true, filter: true },
      { headerName: "Pack Size", field: "size", sortable: true, filter: true },
      {
        headerName: "Count",
        field: "count",
        sortable: true,
        filter: "agNumberColumnFilter",
        cellClass: "ag-numeric-cell",
      },
    ],
    []
  );

  return (
    <Flex flexDirection="column" pt={{ base: "120px", md: "75px" }}>
      <Card mb="20px" p="20px">
        <Flex justify="space-between" align="center" mb="20px">
          <Flex alignItems="center" gap="4">
            <Button
              leftIcon={<FaArrowLeft />}
              onClick={handleBackToDashboard}
              size="sm"
              colorScheme="blue"
              variant="outline"
              borderRadius="5px"
            >
              Back
            </Button>
          </Flex>
          <Flex alignItems="center" gap="10px">
            <Button
              onClick={() => handlePrintReport(detailViewData)}
              size="sm"
              colorScheme="gray"
              variant="outline"
              borderRadius="5px"
              fontSize="sm"
              isDisabled={isLoading || error || detailViewData.length === 0}
            >
              Print Report
            </Button>
            <Button
              onClick={handleExportData}
              size="sm"
              bg="black"
              color="white"
              borderRadius="5px"
              fontSize="sm"
              _hover={{ bg: "gray.700" }}
              isDisabled={isLoading || error || detailViewData.length === 0}
            >
              Export Data
            </Button>
          </Flex>
        </Flex>
        <AgGridWrapper
          rowData={detailViewData}
          columnDefs={columnDefs}
          loading={isLoading}
          height="650px"
          noRowsMessage={error || "No data available"}
          extraGridProps={{ pagination: true, paginationPageSize: 8, headerHeight: 70 }}
        />
      </Card>

      <Modal isOpen={isModalOpen} onClose={handleCloseModal} size="4xl">
        <ModalOverlay />
        <ModalContent maxW="80vw" maxH="90vh">
          <ModalHeader>Additional Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedRowData && selectedRowData.additionalData?.length > 0 ? (
              <AgGridWrapper
                rowData={selectedRowData.additionalData}
                columnDefs={nestedColumnDefs}
                height="700px"
                noRowsMessage="No additional data available"
                extraGridProps={{ pagination: true, paginationPageSize: 8, headerHeight: 70 }}
              />
            ) : (
              <ChakraText>No additional data available.</ChakraText>
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              onClick={handleCloseModal}
              size="md"
              borderRadius="5px"
            >
              Close
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Flex>
  );
}
